import { createContext, useContext } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import apiClient from "@/api/client";
import type {
  AronAccessLevel,
  AronConversation,
  AronMessage,
  AronPendingWrite,
  AronPendingWriteThread,
  AronReasoningMode,
  AronWorkMode,
  SendMessageResponse,
} from "../types";

/**
 * API base for the chat machinery. The classic assistant lives under "/aron"
 * (DLC_03); the ENG persona (DLC_04) reuses the SAME components/hooks under
 * "/eng-aron" by wrapping its page in this provider. Query keys carry the base
 * so the two conversation lists never mix; message keys stay base-independent
 * (conversation UUIDs are globally unique, and the Centrifugo stream patches
 * by conversation id without knowing the base).
 */
export const AronApiBaseContext = createContext<string>("/aron");

/** Która persona siedzi pod tym adresem API. Czat jest jeden, person kilka:
 * "/aron" — asystent firmowy, "/eng-aron" — sprawdzający dokumentację (DLC_04),
 * "/pdf" — asystent PDF (DLC_12). Komponenty rozmowy pytają O TO, zamiast
 * porównywać adresy u siebie (binarne „to nie ARON” gubiło każdą trzecią personę). */
export type AronPersonaUi = "aron" | "eng" | "pdf";

export function useAronPersonaUi(): AronPersonaUi {
  const base = useContext(AronApiBaseContext);
  if (base === "/eng-aron") return "eng";
  if (base === "/pdf") return "pdf";
  return "aron";
}

export function useAronApiBase(): string {
  return useContext(AronApiBaseContext);
}

export const aronKeys = {
  /** Prefix over EVERY cached page of this base's list (all search terms) —
   * what invalidation and cross-list patching key off. */
  conversations: (base: string) => ["aron", "conversations", base] as const,
  /** One list: the base plus the search term it was fetched with ("" = all). */
  conversationList: (base: string, search: string) =>
    ["aron", "conversations", base, "list", search] as const,
  messages: (id: string) => ["aron", "conversations", id, "messages"] as const,
  workModes: (base: string) => ["aron", "work-modes", base] as const,
  usage: (base: string) => ["aron", "usage", base] as const,
  pendingWriteThreads: (base: string) =>
    ["aron", "conversations", base, "pending-writes"] as const,
  /** Pojedyncza rozmowa — dla wątku spoza pierwszej strony listy. Klucz siedzi
   * pod prefiksem "conversations", więc unieważnianie listy odświeża też ją. */
  conversation: (id: string) => ["aron", "conversations", id, "detail"] as const,
};

export function invalidateAronConversations(qc: QueryClient) {
  // Prefix match — covers both personas' lists (and, as before, message caches).
  qc.invalidateQueries({ queryKey: ["aron", "conversations"] });
}

export function invalidateAronUsage(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["aron", "usage"] });
}

export interface AronUsage {
  /** Kwoty jako łańcuchy — backend liczy w Decimalu, JSON nie ma gubić groszy. */
  cost_today_usd: string;
  daily_cost_budget_usd: string;
  remaining_usd: string;
  percent: number;
  /** Objętość pracy obok kwoty; limitem NIE jest. */
  tokens_today: number;
}

/** The current user's own daily SPEND vs the cap (composer indicator). */
export function useAronUsage() {
  const base = useAronApiBase();
  return useQuery<AronUsage>({
    queryKey: aronKeys.usage(base),
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronUsage }>(`${base}/usage/`);
      return r.data.data;
    },
    staleTime: 30_000,
  });
}

/** How many threads one "Pokaż starsze" click brings in. */
const CONVERSATION_PAGE_SIZE = 30;

interface ConversationPage {
  rows: AronConversation[];
  hasNext: boolean;
}

/**
 * The user's own conversations, newest first — paged, with an optional search.
 *
 * Both halves are the SERVER's job. The list outgrows one page within a week of
 * daily use, so filtering the page the client happens to hold would hide exactly
 * the old thread the search exists to find, and without paging the older threads
 * were simply unreachable. `data` is every page loaded so far, flattened.
 */
export function useAronConversations(search = "") {
  const base = useAronApiBase();
  const term = search.trim();
  return useInfiniteQuery({
    queryKey: aronKeys.conversationList(base, term),
    initialPageParam: 1,
    getNextPageParam: (last: ConversationPage, all) =>
      last.hasNext ? all.length + 1 : undefined,
    staleTime: 30_000,
    queryFn: async ({ pageParam }): Promise<ConversationPage> => {
      const qs = new URLSearchParams({
        page: String(pageParam),
        page_size: String(CONVERSATION_PAGE_SIZE),
      });
      if (term) qs.set("search", term);
      const r = await apiClient.get<{
        data: AronConversation[];
        meta?: { next?: string | null };
      }>(`${base}/conversations/?${qs.toString()}`);
      return { rows: r.data.data, hasNext: !!r.data.meta?.next };
    },
    select: (d) => d.pages.flatMap((p) => p.rows),
  });
}

/** Patch one conversation in EVERY cached page of this base's list (each search
 * term is its own cache entry). The list is an infinite query, so its cache holds
 * `{ pages }` — writing a bare array over it would silently corrupt the list.
 * The single-conversation detail is patched too: a thread outside the first list
 * page reads ONLY that cache (useAronConversationInfo), and its key does not sit
 * under the base prefix — without this the chip kept showing the old value. */
function patchAronConversation(
  qc: QueryClient,
  base: string,
  id: string,
  patch: (c: AronConversation) => AronConversation,
) {
  qc.setQueriesData<InfiniteData<ConversationPage>>(
    { queryKey: aronKeys.conversations(base) },
    (old) =>
      old && {
        ...old,
        pages: old.pages.map((p) => ({
          ...p,
          rows: p.rows.map((c) => (c.id === id ? patch(c) : c)),
        })),
      },
  );
  qc.setQueryData<AronConversation>(
    aronKeys.conversation(id),
    (old) => old && patch(old),
  );
}

/**
 * Rozmowa po id — z pierwszej strony listy, a gdy jej tam nie ma, dociągnięta
 * osobno.
 *
 * Lista jest STRONICOWANA (20 pozycji), a otwarta karta, dok albo okno
 * zatwierdzania mogą pokazywać wątek sprzed setek rozmów. Bez tego taka karta
 * nazywa się „Rozmowa", nie zna trybu YOLO ani liczby propozycji zapisu —
 * wygląda jak zepsuta, a jest tylko poza pierwszą stroną.
 */
export function useAronConversationInfo(
  conversationId: string | null,
): AronConversation | undefined {
  const base = useAronApiBase();
  const { data: convs = [] } = useAronConversations();
  const listed = conversationId
    ? convs.find((c) => c.id === conversationId)
    : undefined;
  const { data: fetched } = useQuery({
    queryKey: conversationId
      ? aronKeys.conversation(conversationId)
      : ["aron", "noop-conversation"],
    enabled: !!conversationId && !listed,
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronConversation }>(
        `${base}/conversations/${conversationId}/`,
      );
      return r.data.data;
    },
    staleTime: 30_000,
  });
  return listed ?? fetched;
}

/** Threads with write proposals awaiting my click — mirrors the sidebar badge
 * (unpaginated), so a proposal buried in an old thread is still reachable.
 * Fetched only on the classic assistant ("/aron"): the endpoint spans ALL the
 * user's threads, and rows from other personas would 404 under another base. */
export function useAronPendingWriteThreads() {
  const base = useAronApiBase();
  return useQuery({
    queryKey: aronKeys.pendingWriteThreads(base),
    enabled: base === "/aron",
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronPendingWriteThread[] }>(
        `${base}/conversations/pending-writes/`,
      );
      return r.data.data;
    },
    staleTime: 30_000,
  });
}

export function useAronMessages(conversationId: string | null) {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useQuery({
    queryKey: conversationId ? aronKeys.messages(conversationId) : ["aron", "noop"],
    enabled: !!conversationId,
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronMessage[] }>(
        `${base}/conversations/${conversationId}/messages/`,
      );
      const server = r.data.data;
      const existing =
        qc.getQueryData<AronMessage[]>(aronKeys.messages(conversationId!)) ?? [];
      // While a turn is in flight the cache (optimistic + streamed deltas/thinking)
      // is the source of truth — the DB assistant row is still empty/pending, so a
      // GET would wipe the live progress. Don't clobber it.
      const liveTurn = existing.some(
        (m) =>
          m.role === "assistant" && (m.status === "streaming" || m.status === "pending"),
      );
      if (liveTurn) return existing;
      // Otherwise the server is authoritative; just keep any optimistic temp user
      // message not yet persisted.
      const serverIds = new Set(server.map((m) => m.id));
      const optimistic = existing.filter(
        (m) => m.id.startsWith("temp-") && !serverIds.has(m.id),
      );
      return [...server, ...optimistic];
    },
    refetchOnWindowFocus: false,
  });
}

export interface AronConversationBudget {
  used_usd: string;
  budget_usd: string;
  override_usd: string | null;
  is_over: boolean;
  daily_budget_usd: string;
  suggested_budget_usd: string;
  can_raise: boolean;
  used_tokens: number;
}

const budgetKey = (id: string) =>
  ["aron", "conversations", id, "budget"] as const;

/** Cost-budget status for one conversation — fetched on demand when a turn
 * stopped on the conversation cap (so the banner can show used/limit + raise). */
export function useAronConversationBudget(
  conversationId: string | null,
  enabled: boolean,
) {
  const base = useAronApiBase();
  return useQuery<AronConversationBudget>({
    queryKey: conversationId ? budgetKey(conversationId) : ["aron", "noop-budget"],
    enabled: !!conversationId && enabled,
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronConversationBudget }>(
        `${base}/conversations/${conversationId}/budget/`,
      );
      return r.data.data;
    },
    staleTime: 10_000,
  });
}

/** Jedno ustalenie z notatnika zlecenia (backend: apps/aron/services/notebook.py). */
export interface AronNotebookEntry {
  key: string;
  value: string;
  at: string;
}

const notebookKey = (id: string) =>
  ["aron", "conversations", id, "notebook"] as const;

/** Notatnik zlecenia: fakty, na których ARON opiera decyzje w TEJ rozmowie.
 * Tylko do wglądu — człowiek ma widzieć, czy asystent trzyma się właściwego
 * przedsięwzięcia i właściwej umowy, zanim zacznie na nich pisać. Odświeżany po
 * każdej turze (klucz wisi pod prefiksem rozmowy, więc unieważnia się razem
 * z historią). */
export function useAronNotebook(
  conversationId: string | null,
  { enabled = true, pollMs = 0 }: { enabled?: boolean; pollMs?: number } = {},
) {
  const base = useAronApiBase();
  return useQuery<AronNotebookEntry[]>({
    queryKey: conversationId
      ? notebookKey(conversationId)
      : ["aron", "noop-notebook"],
    enabled: !!conversationId && enabled,
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronNotebookEntry[] }>(
        `${base}/conversations/${conversationId}/notebook/`,
      );
      return r.data.data ?? [];
    },
    // W trakcie tury notatnik rośnie — bez odpytywania człowiek zobaczyłby
    // ustalenia dopiero po odświeżeniu strony.
    refetchInterval: pollMs || false,
    staleTime: 5_000,
  });
}

/** Raise a single conversation's token cap (technical director / CEO only). */
export function useRaiseAronConversationBudget(conversationId: string) {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (costBudgetUsd: string) => {
      const r = await apiClient.post<{ data: AronConversationBudget }>(
        `${base}/conversations/${conversationId}/budget/`,
        { cost_budget_usd: costBudgetUsd },
      );
      return r.data.data;
    },
    onSuccess: (b) => qc.setQueryData(budgetKey(conversationId), b),
  });
}

export function useAronWorkModes() {
  const base = useAronApiBase();
  return useQuery({
    queryKey: aronKeys.workModes(base),
    queryFn: async () => {
      const r = await apiClient.get<{
        data: { reasoning_modes: AronWorkMode[]; access_levels: unknown[] };
      }>(`${base}/work-modes/`);
      return r.data.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateAronConversation() {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      mode: AronReasoningMode;
      accessLevel?: AronAccessLevel;
      autopilot?: boolean;
      unlimitedSteps?: boolean;
      /** Persona context (np. ENG: { project_id, branch, doc_type }). */
      context?: Record<string, string>;
    }) => {
      const r = await apiClient.post<{ data: AronConversation }>(
        `${base}/conversations/`,
        {
          reasoning_mode: vars.mode,
          access_level: vars.accessLevel,
          autopilot: vars.autopilot,
          unlimited_steps: vars.unlimitedSteps,
          ...(vars.context ? { context: vars.context } : {}),
        },
      );
      return r.data.data;
    },
    onSuccess: () => invalidateAronConversations(qc),
  });
}

/** Key of the access switch — the composer holds a send while one is in flight. */
export const ARON_SET_ACCESS_KEY = ["aron", "set-access"] as const;

/** Switch an existing conversation's access level (Tylko odczyt ↔ Zapis) mid-thread.
 * Optimistic so the toggle flips instantly; the change applies from the next turn.
 * The turn reads the level from the database when it starts, so a message sent
 * before this POST lands runs in the OLD mode (META-ARON b101478d) — hence the
 * mutation key the composer watches. */
export function useSetAronAccess() {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ARON_SET_ACCESS_KEY,
    mutationFn: async (vars: {
      conversationId: string;
      accessLevel: AronAccessLevel;
    }) => {
      const r = await apiClient.post<{ data: AronConversation }>(
        `${base}/conversations/${vars.conversationId}/access/`,
        { access_level: vars.accessLevel },
      );
      return r.data.data;
    },
    onMutate: async (vars) => {
      const detailKey = aronKeys.conversation(vars.conversationId);
      await qc.cancelQueries({ queryKey: aronKeys.conversations(base) });
      await qc.cancelQueries({ queryKey: detailKey });
      // Snapshot every cache the patch below touches (one list per search term,
      // plus the detail) — the rollback has to restore them all.
      const prev = [
        ...qc.getQueriesData({ queryKey: aronKeys.conversations(base) }),
        [detailKey, qc.getQueryData(detailKey)] as const,
      ];
      patchAronConversation(qc, base, vars.conversationId, (c) => ({
        ...c,
        access_level: vars.accessLevel,
      }));
      return { prev };
    },
    onError: (_e, _vars, ctx) => {
      for (const [key, data] of ctx?.prev ?? []) qc.setQueryData(key, data);
    },
    onSuccess: (conv) => patchAronConversation(qc, base, conv.id, () => conv),
  });
}

/** Switch an existing conversation's model / reasoning mode mid-thread.
 * Applies from the next message; the backend refuses (409) while a turn is running,
 * so this is NOT optimistic — the chip must not show a model the thread never got. */
export function useSetAronModel() {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      conversationId: string;
      mode: AronReasoningMode;
    }) => {
      const r = await apiClient.post<{ data: AronConversation }>(
        `${base}/conversations/${vars.conversationId}/model/`,
        { reasoning_mode: vars.mode },
      );
      return r.data.data;
    },
    onSuccess: (conv) => {
      // Both caches the composer can read from: the list (thread on page 1) and the
      // single-conversation detail (thread opened from a link / older page).
      patchAronConversation(qc, base, conv.id, () => conv);
      qc.setQueryData(aronKeys.conversation(conv.id), conv);
    },
  });
}

export function useDeleteAronConversation() {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${base}/conversations/${id}/`);
    },
    onSuccess: () => invalidateAronConversations(qc),
  });
}

/** Show the user's message in the transcript INSTANTLY (before the POST), then
 * reconcile with the real row in useSendAronMessage.onSuccess. Returns the temp id. */
export function addOptimisticUserMessage(
  qc: QueryClient,
  conversationId: string,
  content: string,
  files: File[] = [],
): string {
  const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  // Preview attachments instantly via local object URLs; the server response
  // replaces this temp message with the persisted one (real storage URLs).
  const attachments = files.map((f, i) => ({
    id: `${tempId}-att-${i}`,
    filename: f.name,
    content_type: f.type,
    url: URL.createObjectURL(f),
  }));
  qc.setQueryData<AronMessage[]>(aronKeys.messages(conversationId), (old = []) => [
    ...old,
    {
      id: tempId,
      role: "user",
      text: content,
      status: "done",
      created_at: new Date().toISOString(),
      tool_steps: [],
      sources: [],
      attachments,
    },
  ]);
  return tempId;
}

/** Powód odrzucenia wysyłki, po polsku, prosto od backendu. Odpowiedzi błędów
 *  mają kształt { data, errors, meta } — komunikat siedzi w `errors.detail`
 *  (np. „Nieobsługiwany typ pliku…", „Wyczerpano dzienny limit tokenów…"). */
export function aronErrorText(err: unknown): string {
  const res = (err as { response?: { status?: number; data?: unknown } })?.response;
  const errors = (res?.data as { errors?: unknown } | undefined)?.errors;
  if (typeof errors === "string" && errors.trim()) return errors;
  if (errors && typeof errors === "object") {
    const detail = (errors as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    // Błędy pól serializera: { content: ["…"] } — bierzemy pierwszy sensowny.
    for (const v of Object.values(errors as Record<string, unknown>)) {
      if (typeof v === "string" && v.trim()) return v;
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
    }
  }
  if (res?.status === 409) return "Poprzednia odpowiedź jest jeszcze w toku. Poczekaj na jej zakończenie.";
  if (!res) return "Brak połączenia z serwerem — wiadomość nie została wysłana.";
  return `Nie udało się wysłać wiadomości (błąd ${res.status}).`;
}

/** Server's own words for a refused conversation switch — model (409 „poczekaj na
 *  zakończenie", 400 „tryb niedostępny") or access level (403 „zapis wyłączony") —
 *  the reason is always more useful than a generic failure. */
export function switchErrorText(err: unknown, fallback: string): string {
  const data = (
    err as { response?: { data?: { errors?: unknown; detail?: unknown } } }
  )?.response?.data;
  const payload = data?.errors ?? data?.detail;
  if (typeof payload === "string" && payload.trim()) return payload;
  if (Array.isArray(payload) && payload.length) return payload.map(String).join(" ");
  if (payload && typeof payload === "object") {
    const flat = Object.values(payload as Record<string, unknown>)
      .flatMap((v) => (Array.isArray(v) ? v.map(String) : [String(v)]))
      .join(" ")
      .trim();
    if (flat) return flat;
  }
  return fallback;
}

/** Wysyłka padła — zostawiamy wiadomość w wątku (żeby nie zgubić wpisanego
 *  tekstu), ale WIDOCZNIE oznaczoną jako niewysłaną. Wcześniej błąd był łykany
 *  po cichu i wyglądało to tak, jakby ARON „przestał myśleć". */
export function markOptimisticMessageFailed(
  qc: QueryClient,
  conversationId: string,
  tempId: string,
  reason: string,
): void {
  qc.setQueryData<AronMessage[]>(aronKeys.messages(conversationId), (old = []) =>
    old.map((m) => (m.id === tempId ? { ...m, status: "error" as const, error: reason } : m)),
  );
}

export function useSendAronMessage() {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      conversationId: string;
      content: string;
      tempUserId?: string;
      files?: File[];
      /** Wysłać obrazy przeskalowane (decyzja z pola wyboru przy TYM zapytaniu). */
      compressImages?: boolean;
      pageContext?: import("../utils/pageContext").PageContext;
    }) => {
      const url = `${base}/conversations/${vars.conversationId}/messages/`;
      // Multipart when there are attachments (image/PDF); JSON otherwise.
      if (vars.files && vars.files.length > 0) {
        const fd = new FormData();
        fd.append("content", vars.content);
        if (vars.compressImages) fd.append("compress_images", "true");
        if (vars.pageContext) fd.append("page_context", JSON.stringify(vars.pageContext));
        vars.files.forEach((f) => fd.append("files", f));
        // The apiClient default is Content-Type: application/json — must override for
        // multipart or axios drops the files (same pattern as every other upload here).
        const r = await apiClient.post<{ data: SendMessageResponse }>(url, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return r.data.data;
      }
      const r = await apiClient.post<{ data: SendMessageResponse }>(url, {
        content: vars.content,
        ...(vars.pageContext ? { page_context: vars.pageContext } : {}),
      });
      return r.data.data;
    },
    onSuccess: (res, vars) => {
      qc.setQueryData<AronMessage[]>(aronKeys.messages(vars.conversationId), (old = []) => {
        // Replace the optimistic temp user message with the real persisted row…
        const mapped = vars.tempUserId
          ? old.map((m) => (m.id === vars.tempUserId ? res.user_message : m))
          : old;
        const base_ = mapped.some((m) => m.id === res.user_message.id)
          ? mapped
          : [...mapped, res.user_message];
        // …and add an in-flight assistant placeholder (the stream fills it), unless
        // the stream already created it (it ensures it on message.start).
        return base_.some((m) => m.id === res.assistant_message_id)
          ? base_
          : [
              ...base_,
              {
                id: res.assistant_message_id,
                role: "assistant",
                text: "",
                status: "streaming",
                created_at: new Date().toISOString(),
                tool_steps: [],
                sources: [],
              },
            ];
      });
      invalidateAronConversations(qc);
    },
  });
}

export function useStopAronTurn() {
  const base = useAronApiBase();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      await apiClient.post(`${base}/conversations/${conversationId}/stop/`, {});
    },
  });
}

/** Merge an updated pending write back into whichever message holds it. */
function patchPendingWrite(
  qc: QueryClient,
  conversationId: string,
  write: AronPendingWrite,
) {
  qc.setQueryData<AronMessage[]>(aronKeys.messages(conversationId), (old = []) =>
    old.map((m) =>
      (m.pending_writes ?? []).some((w) => w.id === write.id)
        ? {
            ...m,
            pending_writes: (m.pending_writes ?? []).map((w) =>
              w.id === write.id ? write : w,
            ),
          }
        : m,
    ),
  );
}

/** Faza 3: approve + execute a proposed write (the only path that mutates data).
 * An optional `body` (and `followUps`) lets a review modal or the developer JSON
 * editor submit the user-edited payload; an optional `chainAction` runs a follow-up
 * detail action (e.g. "approve") right after a successful create — as the approver,
 * with their real permissions. */
export function useExecuteAronWrite() {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      conversationId: string;
      writeId: string;
      body?: Record<string, unknown>;
      followUps?: Record<string, unknown>[];
      chainAction?: string;
    }) => {
      const r = await apiClient.post<{ data: AronPendingWrite }>(
        `${base}/conversations/${vars.conversationId}/writes/${vars.writeId}/execute/`,
        {
          ...(vars.body ? { body: vars.body } : {}),
          ...(vars.followUps ? { follow_ups: vars.followUps } : {}),
          ...(vars.chainAction ? { chain_action: vars.chainAction } : {}),
        },
      );
      return r.data.data;
    },
    onSuccess: (write, vars) => {
      patchPendingWrite(qc, vars.conversationId, write);
      // The conversation row carries a "do zatwierdzenia" badge — refresh the
      // list (exact key, so in-flight message caches are left alone).
      qc.invalidateQueries({ queryKey: aronKeys.conversations(base) });
    },
  });
}

/** „Poproś o poprawkę" po ocenie recenzenta — decyzja człowieka, nie automatu.
 *
 * Tura startuje na zapleczu i dopisuje się POD poprzednią odpowiedzią (nic nie znika).
 * Nowe wiersze pojawiają się dopiero po unieważnieniu pamięci podręcznej — bez tego
 * żądanie przechodziło, tura ruszała, a użytkownik widział bezruch i klikał drugi raz.
 */
export function useAronRetryWithCritique() {
  const qc = useQueryClient();
  const base = useAronApiBase();
  return useMutation({
    mutationFn: async (vars: { conversationId: string; critique: string }) => {
      const r = await apiClient.post(
        `${base}/conversations/${vars.conversationId}/retry-with-critique/`,
        { critique: vars.critique },
      );
      return r.data.data as { queued: boolean; assistant_message_id: string };
    },
    onSuccess: (_dane, vars) => {
      qc.invalidateQueries({ queryKey: aronKeys.messages(vars.conversationId) });
      qc.invalidateQueries({ queryKey: aronKeys.conversations(base) });
    },
  });
}

/** Re-run the follow-up steps that did NOT go through on an executed write.
 * Only the failed tail runs again — successful steps are never repeated. */
export function useRetryAronWriteSteps() {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { conversationId: string; writeId: string }) => {
      const r = await apiClient.post<{ data: AronPendingWrite }>(
        `${base}/conversations/${vars.conversationId}/writes/${vars.writeId}/retry-steps/`,
        {},
      );
      return r.data.data;
    },
    onSuccess: (write, vars) => {
      patchPendingWrite(qc, vars.conversationId, write);
      qc.invalidateQueries({ queryKey: aronKeys.messages(vars.conversationId) });
    },
  });
}

/** Faza 3: reject a proposed write (nothing is executed). */
export function useRejectAronWrite() {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { conversationId: string; writeId: string }) => {
      const r = await apiClient.post<{ data: AronPendingWrite }>(
        `${base}/conversations/${vars.conversationId}/writes/${vars.writeId}/reject/`,
        {},
      );
      return r.data.data;
    },
    onSuccess: (write, vars) => {
      patchPendingWrite(qc, vars.conversationId, write);
      qc.invalidateQueries({ queryKey: aronKeys.conversations(base) });
    },
  });
}

/** Włącz/wyłącz Tryb samodzielny (YOLO) dla istniejącej rozmowy. Backend bramkuje
 * uprawnieniem (flaga albo CEO) i wymaga trybu Zapis. */
export function useSetAronAutopilot() {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { conversationId: string; enabled: boolean }) => {
      const r = await apiClient.post<{ data: AronConversation }>(
        `${base}/conversations/${vars.conversationId}/autopilot/`,
        { enabled: vars.enabled },
      );
      return r.data.data;
    },
    onSuccess: () => invalidateAronConversations(qc),
  });
}

/** Włącz/wyłącz „Wyłącz limit kroków" dla istniejącej rozmowy. Backend bramkuje
 * tym samym uprawnieniem co YOLO (flaga albo CEO); działa w każdym trybie dostępu. */
export function useSetAronUnlimitedSteps() {
  const base = useAronApiBase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { conversationId: string; enabled: boolean }) => {
      const r = await apiClient.post<{ data: AronConversation }>(
        `${base}/conversations/${vars.conversationId}/unlimited-steps/`,
        { enabled: vars.enabled },
      );
      return r.data.data;
    },
    onSuccess: () => invalidateAronConversations(qc),
  });
}
